import React, { useState, useEffect, useRef } from 'react';
import { Box, styled, Typography, IconButton, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendConversation, ConversationResponse } from '@services/discover';
import { getCurrentUserId } from '@utils/userUtils';

// ─── Styled Components ───────────────────────────────────────────────────────

const ChatContainer = styled(Box)({
  height: '99vh',
  backgroundColor: '#f4fbfd',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: '"Rethink Sans"',
});

const ChatHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #35d7e4',
  padding: '0 48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '72px',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  gap: '12px',
  [theme.breakpoints.down('md')]: { padding: '0 24px' },
  [theme.breakpoints.down('sm')]: { padding: '0 16px' },
}));

const BackButton = styled(IconButton)({
  backgroundColor: '#eafdff',
  border: '1px solid #99d4e1',
  borderRadius: '8px',
  padding: '8px',
  flexShrink: 0,
  '&:hover': { backgroundColor: '#d8f0f4' },
});

const ChatTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#1a1a1a',
  letterSpacing: '-0.3px',
  fontFamily: '"Rethink Sans"',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const SourceBanner = styled(Box)({
  backgroundColor: '#e6f9fc',
  borderBottom: '1px solid #b8edf5',
  padding: '8px 48px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '24px 48px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  maxWidth: '900px',
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: { padding: '16px 24px' },
  [theme.breakpoints.down('sm')]: { padding: '12px 16px' },
}));

const MessageRow = styled(Box)<{ isUser?: boolean }>(({ isUser }) => ({
  display: 'flex',
  flexDirection: isUser ? 'row-reverse' : 'row',
  alignItems: 'flex-end',
  gap: '10px',
}));

const Avatar = styled(Box)<{ isUser?: boolean }>(({ isUser }) => ({
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: isUser ? '#0a829d' : '#e6f9fc',
  color: isUser ? '#fff' : '#0a829d',
  border: isUser ? 'none' : '1px solid #b8edf5',
}));

const BubbleWrapper = styled(Box)<{ isUser?: boolean }>(({ isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
  maxWidth: '75%',
  gap: '4px',
}));

const Bubble = styled(Box)<{ isUser?: boolean }>(({ isUser }) => ({
  padding: '12px 16px',
  borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
  backgroundColor: isUser ? '#0a829d' : '#ffffff',
  color: isUser ? '#fff' : '#1f2937',
  fontSize: '14px',
  lineHeight: 1.7,
  whiteSpace: 'pre-line',
  boxShadow: isUser ? 'none' : '0 2px 12px rgba(5,89,108,0.08)',
  border: isUser ? 'none' : '1px solid #d8f5fb',
  fontFamily: '"Rethink Sans"',
  '& strong': { fontWeight: 600 },
}));

const BubbleTime = styled(Typography)({
  fontSize: '10px',
  color: '#9ca3af',
  fontFamily: '"Rethink Sans"',
  padding: '0 4px',
});

const SourcesToggle = styled(Box)({
  marginTop: '8px',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #e0f5fb',
  backgroundColor: '#f8feff',
  maxWidth: '100%',
});

const SourcesToggleHeader = styled(Box)({
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { backgroundColor: '#edf9fc' },
});

const SourceItem = styled(Box)({
  padding: '8px 12px',
  borderTop: '1px solid #e0f5fb',
  '&:first-of-type': { borderTop: 'none' },
});

const SourceLink = styled('a')({
  fontSize: '11px',
  color: '#0a829d',
  textDecoration: 'none',
  wordBreak: 'break-all',
  display: 'block',
  marginTop: '2px',
  '&:hover': { textDecoration: 'underline' },
});

const FollowUpChips = styled(Box)({
  marginTop: '10px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
});

const FollowUpChip = styled(Box)({
  padding: '6px 12px',
  backgroundColor: '#fff',
  border: '1px solid #b8edf5',
  borderRadius: '20px',
  fontSize: '12px',
  color: '#0a829d',
  cursor: 'pointer',
  fontFamily: '"Rethink Sans"',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#e6f9fc',
    borderColor: '#0a829d',
  },
});

// Animated typing dots
const TypingDots = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 0',
  '& span': {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: '#0a829d',
    display: 'inline-block',
    opacity: 0.4,
    animation: 'bounce 1.2s infinite ease-in-out',
    '&:nth-of-type(1)': { animationDelay: '0s' },
    '&:nth-of-type(2)': { animationDelay: '0.2s' },
    '&:nth-of-type(3)': { animationDelay: '0.4s' },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': { opacity: 0.4, transform: 'scale(0.9)' },
    '40%': { opacity: 1, transform: 'scale(1.15)' },
  },
});

const EmptyState = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '32px',
  textAlign: 'center',
});

const SuggestionCard = styled(Box)({
  padding: '12px 16px',
  backgroundColor: '#fff',
  border: '1px solid #b8edf5',
  borderRadius: '10px',
  fontSize: '13px',
  color: '#0a829d',
  cursor: 'pointer',
  fontFamily: '"Rethink Sans"',
  transition: 'all 0.15s ease',
  textAlign: 'left',
  width: '100%',
  maxWidth: '380px',
  '&:hover': {
    backgroundColor: '#e6f9fc',
    borderColor: '#0a829d',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(10,130,157,0.1)',
  },
});

const ChatInput = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderTop: '1px solid #d8f5fb',
  padding: '16px 48px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.down('md')]: { padding: '16px 24px' },
  [theme.breakpoints.down('sm')]: { padding: '12px 16px' },
}));

const InputField = styled(TextField)({
  flex: 1,
  fontFamily: '"Rethink Sans"',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8feff',
    border: '1px solid #99d4e1',
    '& fieldset': { border: 'none' },
    '&:hover': { backgroundColor: '#f0fafc' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 3px rgba(10,130,157,0.1)',
    },
    '& input': {
      padding: '14px 12px',
      fontSize: '14px',
      fontFamily: '"Rethink Sans"',
    },
  },
});

const SendButton = styled(IconButton)({
  backgroundColor: '#0a829d',
  color: '#ffffff',
  borderRadius: '10px',
  padding: '12px',
  minWidth: '46px',
  minHeight: '46px',
  transition: 'all 0.15s ease',
  '&:hover': { backgroundColor: '#086e86', transform: 'scale(1.05)' },
  '&:disabled': { backgroundColor: '#d1d5db', transform: 'none' },
});

const InlineError = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '10px 14px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '10px',
  maxWidth: '75%',
  alignSelf: 'flex-start',
  marginLeft: '44px',
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function DiscoverChat() {
  const location = useLocation();
  const navigate = useNavigate();

  const linkUrl: string | undefined = location.state?.linkUrl;
  const linkName: string | undefined = location.state?.linkName;
  const linkId: string | undefined = location.state?.linkId;
  const fromArticle: boolean = location.state?.fromArticle;
  const initialConversationData: ConversationResponse | undefined = location.state?.conversationData;
  const initialConversationId: string | undefined = location.state?.conversationId;

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    type: 'user' | 'ai' | 'error';
    content: string;
    timestamp: Date;
    sources?: any[];
    suggestedFollowUps?: string[];
    sourcesOpen?: boolean;
  }>>([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Get user initials for avatar
  const userInitials = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        const fn = u.first_name || u.firstName || '';
        const ln = u.last_name || u.lastName || '';
        return `${fn[0] || ''}${ln[0] || ''}`.toUpperCase() || 'U';
      }
    } catch { }
    return 'U';
  })();

  // Get display domain from URL
  const sourceDomain = (() => {
    if (!linkUrl) return null;
    try { return new URL(linkUrl).hostname.replace('www.', ''); }
    catch { return linkUrl; }
  })();

  // Suggestions shown in empty state
  const suggestions = [
    'What are the key compliance requirements mentioned?',
    'Summarize the most important updates',
    'What regulatory deadlines should I be aware of?',
    'What actions should my organization take?',
  ];

  // Seed initial conversation if provided (e.g. from article page - response already fetched, no second API call)
  useEffect(() => {
    const initialQuestion = (location.state as any)?.initialQuestion as string | undefined;
    if (initialConversationData && initialConversationId) {
      setConversationId(initialConversationId);
      const messages: typeof chatMessages = [];
      if (initialQuestion) {
        messages.push({
          id: Date.now().toString(),
          type: 'user',
          content: initialQuestion,
          timestamp: new Date(),
        });
      }
      messages.push({
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: initialConversationData.summary,
        timestamp: new Date(),
        sources: initialConversationData.sources,
        suggestedFollowUps: initialConversationData.suggested_follow_ups,
        sourcesOpen: false,
      });
      setChatMessages(messages);
    } else if (initialQuestion && linkUrl) {
      // No pre-fetched response (e.g. from discover details) - send question once on load
      sendQuestion(initialQuestion);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, loading]);

  const handleBackClick = () => {
    if (fromArticle && linkId) {
      navigate(`/dashboard/discover/article/${linkId}`, {
        state: {
          category: location.state?.category,
          article: location.state?.article || null,
          url: location.state?.url || null,
          linkName: location.state?.discoverLinkName || null,
        }
      });
    } else {
      navigate('/dashboard/discover');
    }
  };

  const sendQuestion = async (q: string) => {
    if (!q.trim() || !linkUrl || loading) return;

    const userId = getCurrentUserId();
    if (userId === '0') {
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'error',
        content: 'User not authenticated. Please log in again.',
        timestamp: new Date(),
      }]);
      return;
    }

    setChatMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'user',
      content: q.trim(),
      timestamp: new Date(),
    }]);
    setQuestion('');
    setLoading(true);

    try {
      const response = await sendConversation({
        user_id: parseInt(userId),
        industry_type: 'healthcare',
        query: q.trim(),
        url: linkUrl,
        conversation_id: conversationId || null,
      });

      if (response.conversation_id) setConversationId(response.conversation_id);

      setChatMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.summary,
        timestamp: new Date(),
        sources: response.sources,
        suggestedFollowUps: response.suggested_follow_ups,
        sourcesOpen: false,
      }]);
    } catch (error: any) {
      const msg = error?.message || 'Failed to get a response. Please try again.';
      setChatMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'error',
        content: msg,
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSources = (msgId: string) => {
    setChatMessages(prev => prev.map(m => m.id === msgId ? { ...m, sourcesOpen: !m.sourcesOpen } : m));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion(question);
    }
  };

  return (
    <ChatContainer>
      {/* Header */}
      <ChatHeader>
        <BackButton onClick={handleBackClick}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <ChatTitle>{linkName || 'AI Chat'}</ChatTitle>
      </ChatHeader>

      {/* Source context banner */}
      {sourceDomain && (
        <SourceBanner>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="#0a829d" strokeWidth="1.5"/>
            <path d="M10 6v4l2.5 2.5" stroke="#0a829d" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <Typography sx={{ fontSize: '12px', color: '#065b70', fontFamily: '"Rethink Sans"' }}>
            Discussing content from&nbsp;
            <Box component="span" sx={{ fontWeight: 600 }}>{sourceDomain}</Box>
          </Typography>
        </SourceBanner>
      )}

      {/* Messages */}
      <ChatMessages>

        {/* Empty state */}
        {chatMessages.length === 0 && !loading && (
          <EmptyState>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#e6f9fc', border: '1px solid #b8edf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" fill="#0a829d"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', fontFamily: '"Rethink Sans"' }}>
              Ask anything about this source
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6b7280', fontFamily: '"Rethink Sans"', maxWidth: 320 }}>
              I can answer compliance questions, summarize updates, and help you understand regulatory requirements.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', alignItems: 'center', mt: 1 }}>
              {suggestions.map((s, i) => (
                <SuggestionCard key={i} onClick={() => sendQuestion(s)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={{ color: '#0a829d', flexShrink: 0 }}>›</Box>
                    {s}
                  </Box>
                </SuggestionCard>
              ))}
            </Box>
          </EmptyState>
        )}

        {/* Messages */}
        {chatMessages.map((msg) => {
          if (msg.type === 'error') {
            return (
              <InlineError key={msg.id}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="10" cy="10" r="8" stroke="#dc2626" strokeWidth="1.5"/>
                  <path d="M10 6v5M10 13v1" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <Typography sx={{ fontSize: '13px', color: '#dc2626', fontFamily: '"Rethink Sans"', lineHeight: 1.5 }}>
                  {msg.content}
                </Typography>
              </InlineError>
            );
          }

          const isUser = msg.type === 'user';
          return (
            <MessageRow key={msg.id} isUser={isUser}>
              <Avatar isUser={isUser}>
                {isUser ? userInitials : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" fill="#0a829d"/>
                  </svg>
                )}
              </Avatar>

              <BubbleWrapper isUser={isUser}>
                <Bubble isUser={isUser}>
                  <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                </Bubble>

                <BubbleTime>
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </BubbleTime>

                {/* Follow-up chips */}
                {!isUser && msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
                  <FollowUpChips>
                    {msg.suggestedFollowUps.map((fu, i) => (
                      <FollowUpChip key={i} onClick={() => sendQuestion(fu)}>
                        <span>›</span> {fu}
                      </FollowUpChip>
                    ))}
                  </FollowUpChips>
                )}

                {/* Sources collapsible */}
                {!isUser && msg.sources && msg.sources.length > 0 && (
                  <SourcesToggle>
                    <SourcesToggleHeader onClick={() => toggleSources(msg.id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="8" stroke="#0a829d" strokeWidth="1.5"/>
                          <path d="M10 7v6M7 10h6" stroke="#0a829d" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#0a829d', fontFamily: '"Rethink Sans"' }}>
                          {msg.sources.length} source{msg.sources.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ transform: msg.sourcesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <path d="M5 7l5 5 5-5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </SourcesToggleHeader>

                    {msg.sourcesOpen && msg.sources.map((src: any, i: number) => (
                      <SourceItem key={i}>
                        {src.title && (
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1f2937', fontFamily: '"Rethink Sans"' }}>
                            {src.title}
                          </Typography>
                        )}
                        {src.description && (
                          <Typography sx={{ fontSize: '11px', color: '#6b7280', fontFamily: '"Rethink Sans"', mt: '2px' }}>
                            {src.description}
                          </Typography>
                        )}
                        {src.url && (
                          <SourceLink href={src.url} target="_blank" rel="noopener noreferrer">
                            {src.url}
                          </SourceLink>
                        )}
                      </SourceItem>
                    ))}
                  </SourcesToggle>
                )}
              </BubbleWrapper>
            </MessageRow>
          );
        })}

        {/* Typing indicator */}
        {loading && (
          <MessageRow>
            <Avatar>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" fill="#0a829d"/>
              </svg>
            </Avatar>
            <Bubble>
              <TypingDots>
                <span /><span /><span />
              </TypingDots>
            </Bubble>
          </MessageRow>
        )}

        <div ref={messagesEndRef} />
      </ChatMessages>

      {/* Input */}
      <ChatInput>
        <InputField
          placeholder="Ask a question about this source..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          autoComplete="off"
        />
        <SendButton onClick={() => sendQuestion(question)} disabled={loading || !question.trim()}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M3.69362 10.0002L0.299335 1.27199C0.0630953 0.664515 0.655438 0.0835794 1.2414 0.290074L1.33375 0.329333L19.3337 9.32933C19.852 9.58845 19.8844 10.298 19.4309 10.6134L19.3337 10.671L1.33375 19.671C0.750768 19.9625 0.117464 19.4265 0.268798 18.8239L0.299335 18.7283L3.69362 10.0002Z" fill="white"/>
          </svg>
        </SendButton>
      </ChatInput>
    </ChatContainer>
  );
}
